import { useListOrganizationsQuery } from "@/features/organizations/api/organizationsApi";
import { useAssignToOrgMutation } from "@/features/users/api/usersApi";
import { handleMutationError } from "@/shared/api/handleMutationError";
import { notify } from "@/shared/components/notify";
import { roleSelectOptions } from "@/shared/i18n/roleOptions";
import {
  type AssignableRole,
  AssignableRoleSchema,
  type UserResponse,
} from "@keystone/contracts/users";
import { Button, Group, Modal, Select, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface AssignOrgMembershipModalProps {
  user: UserResponse;
  opened: boolean;
  onClose: () => void;
}

interface FormValues {
  orgId: string;
  role: AssignableRole | "";
}

export function AssignOrgMembershipModal({ user, opened, onClose }: AssignOrgMembershipModalProps) {
  const { t } = useTranslation("users");
  const { data: orgs, isLoading: orgsLoading } = useListOrganizationsQuery();
  const [assignToOrg, { isLoading }] = useAssignToOrgMutation();

  const memberOrgIds = useMemo(
    () => new Set(user.orgMemberships.map((m) => m.orgId)),
    [user.orgMemberships],
  );

  const orgOptions = useMemo(
    () =>
      (orgs?.items ?? [])
        .filter((o) => !memberOrgIds.has(o.id))
        .map((o) => ({ value: o.id, label: o.name })),
    [orgs, memberOrgIds],
  );

  const form = useForm<FormValues>({
    initialValues: { orgId: "", role: "" },
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = form.onSubmit(async (values) => {
    let valid = true;
    if (!values.orgId) {
      form.setFieldError("orgId", t("form.errors.organizationRequired"));
      valid = false;
    }
    if (!values.role) {
      form.setFieldError("role", t("form.errors.roleRequired"));
      valid = false;
    }
    if (!valid || !values.role) return;

    try {
      await assignToOrg({
        id: user.id,
        body: { orgId: values.orgId, role: values.role },
      }).unwrap();
      notify.success("users:notifications.orgAssigned");
      handleClose();
    } catch (e) {
      handleMutationError(e, "users:errors.orgAssignFailed");
    }
  });

  return (
    <Modal opened={opened} onClose={handleClose} title={t("detail.actions.assignOrg")} centered>
      <form onSubmit={handleSubmit} noValidate>
        <Stack gap="sm">
          <Select
            label={t("form.organization")}
            placeholder={t("form.organizationPlaceholder")}
            data={orgOptions}
            value={form.values.orgId || null}
            onChange={(value) => form.setFieldValue("orgId", value ?? "")}
            error={form.errors.orgId}
            required
            searchable
            disabled={orgsLoading}
            nothingFoundMessage={t("form.noOrgsAvailable")}
          />
          <Select
            label={t("form.role")}
            placeholder={t("form.rolePlaceholder")}
            data={roleSelectOptions(AssignableRoleSchema.options, t)}
            value={form.values.role || null}
            onChange={(value) => form.setFieldValue("role", (value as AssignableRole) ?? "")}
            error={form.errors.role}
            required
            searchable
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={handleClose} type="button">
              {t("common:actions.cancel")}
            </Button>
            <Button type="submit" loading={isLoading}>
              {t("detail.actions.assignOrg")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
